import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"

/**
 * Informative indicator — shows state, never acts. No hover background, no
 * button border, no pointer cursor; the meaning lives in the tooltip.
 * Clickable things use the templates in `pressable.tsx` / `Button` instead.
 */
export const infoIconVariants = cva(
  "inline-flex shrink-0 items-center justify-center rounded-sm outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50",
  {
    variants: {
      /** `plain`: bare icon; `circle`: icon or avatar in a soft disc. */
      shape: {
        plain: "[&_svg]:size-3.5",
        circle: "size-7 rounded-full bg-muted [&_svg]:size-3.5",
      },
    },
    defaultVariants: { shape: "plain" },
  }
)

export type InfoIconProps = Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  "title"
> &
  VariantProps<typeof infoIconVariants> & {
    /** What the indicator means; shown as a tooltip and read by screen readers. */
    label: React.ReactNode
    /** Accessible name when the indicator has no visible text (icon or dot only). */
    ariaLabel?: string
  }

export const InfoIcon = React.forwardRef<HTMLSpanElement, InfoIconProps>(
  function InfoIcon(
    { className, shape, label, ariaLabel, children, ...props },
    ref
  ) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            ref={ref}
            role="img"
            tabIndex={0}
            aria-label={
              ariaLabel ?? (typeof label === "string" ? label : undefined)
            }
            className={cn(infoIconVariants({ shape }), className)}
            {...props}
          >
            {children}
          </span>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    )
  }
)
